import { useLazyQuery } from "@apollo/client";
import { type VariablesOf } from "@graphql-typed-document-node/core";
import { getRouteApi, Link } from "@tanstack/react-router";
import { createColumnHelper } from "@tanstack/react-table";
import { ExternalLink } from "lucide-react";
import { useEffect } from "react";
import { sourcesSummaryQuery } from "../queries";
import { SUMMARY_SORT_FIELDS, type SummarySortField } from "../shared/sortFields";
import { transformSummaryTableData, type SummaryTableData } from "../utils/summaryTableData";
import { DataTable } from "@/components/DataTable";
import { useUrlTableSorting } from "@/components/DataTable/useUrlTableSorting";
import { usePagination } from "@/components/Pagination/usePagination";
import { Tooltip } from "@/components/Tooltip";
import { useActiveBusinessUnit } from "@/hooks/use-active-business-unit";
import { useToast } from "@/hooks/use-toast";
import { getErrorMessage } from "@/lib/utils/errors";
import { formatInteger } from "@/lib/utils/formatting";

type SortingField = NonNullable<NonNullable<VariablesOf<typeof sourcesSummaryQuery>["sortBy"]>[0]>["field"];

const routeApi = getRouteApi("/(authed)/_layout/bookings/_tabbed/summary");

interface SourcesTableProps {
  fromDate: string;
  toDate: string;
}

export function SourcesTable({ fromDate, toDate }: SourcesTableProps) {
  const [getSources, { loading, data, error, refetch }] = useLazyQuery(sourcesSummaryQuery, {
    fetchPolicy: "cache-and-network",
  });
  const { toast } = useToast();
  const { activeBusinessUnitId } = useActiveBusinessUnit();
  const navigate = routeApi.useNavigate();
  const { sortField, sortDesc } = routeApi.useSearch();

  const pagination = usePagination({
    totalCount: data?.supplyChainBookingSourcesSummary?.connection?.numPartners || 0,
    pageInfo: data?.supplyChainBookingSourcesSummary?.connection?.pageInfo,
    loading,
  });

  const { sortBy, onSortingChange, sorting } = useUrlTableSorting<SummarySortField>({
    initialSort: { id: "BUSINESS_NAME", desc: false },
    validFields: SUMMARY_SORT_FIELDS,
    sortParams: { sortField, sortDesc },
    onSortParamsChange: (field, desc) => {
      void navigate({ search: (prev) => ({ ...prev, sortField: field, sortDesc: desc }) });
    },
  });

  useEffect(() => {
    const variables: VariablesOf<typeof sourcesSummaryQuery> = {
      businessUnit: activeBusinessUnitId || "",
      fromDate,
      toDate,
      sortBy,
      first: pagination.paginationQueryVariables.first,
      last: pagination.paginationQueryVariables.last,
      after: pagination.paginationQueryVariables.after,
      before: pagination.paginationQueryVariables.before,
    };

    const fetchData = async () => {
      await getSources({
        variables,
      });
    };

    fetchData().catch((error: unknown) => {
      toast({
        variant: "destructive",
        title: "Uh oh! Something went wrong.",
        description: getErrorMessage(error) || "There was a problem with your request.",
        action: { label: "Try again", onClick: () => refetch() },
      });
    });
  }, [
    pagination.paginationQueryVariables.first,
    pagination.paginationQueryVariables.last,
    pagination.paginationQueryVariables.after,
    pagination.paginationQueryVariables.before,
    getSources,
    toast,
    refetch,
    activeBusinessUnitId,
    fromDate,
    toDate,
    sortBy,
  ]);

  const tableData = transformSummaryTableData(data?.supplyChainBookingSourcesSummary?.connection?.edges || []);

  return (
    <DataTable
      tableId="sourcesSummaryTable"
      columns={columns}
      data={tableData}
      sortConfig={{ onSortingChange, sorting }}
      loading={loading && !data}
      errorMessage={error?.message}
      pagination={pagination}
    />
  );
}

const { accessor } = createColumnHelper<SummaryTableData>();

const columns = [
  accessor("feedyardName", {
    id: "BUSINESS_NAME" satisfies SortingField,
    header: "Source",
    cell: (info) => {
      const sourceName = info.getValue();
      const contactId = info.row.original.id;

      return contactId ? (
        <Tooltip label="View contact details" asChild>
          <Link
            to="/contacts/$contactId"
            params={{ contactId }}
            onClick={(e) => e.stopPropagation()}
            className="flex items-center gap-2 cursor-pointer group hover:text-primary min-w-0"
          >
            <span className="flex-1 min-w-0 truncate">{sourceName}</span>
            <ExternalLink className="w-3 h-3 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground" />
          </Link>
        </Tooltip>
      ) : (
        <span className="truncate">{sourceName}</span>
      );
    },
    enableSorting: true,
  }),
  accessor("monthlyCommitment", {
    id: "monthlyCommitment",
    header: "Monthly Commitment",
    cell: (info) => {
      const value = info.getValue();
      return value ? formatInteger(value) : "—";
    },
  }),
  accessor("headCountEntered", {
    id: "NUMBER_OF_HEAD" satisfies SortingField,
    header: "Head Count Entered",
    cell: (info) => formatInteger(info.getValue()),
    enableSorting: true,
  }),
  accessor("planned", {
    id: "NUMBER_OF_HEAD_PLANNED" satisfies SortingField,
    header: "Planned",
    cell: (info) => formatInteger(info.getValue()),
    enableSorting: true,
  }),
  accessor("booked", {
    id: "NUMBER_OF_HEAD_BOOKED" satisfies SortingField,
    header: "Booked",
    cell: (info) => formatInteger(info.getValue()),
    enableSorting: true,
  }),
  accessor("shipped", {
    id: "NUMBER_OF_HEAD_SHIPPED" satisfies SortingField,
    header: "Shipped",
    cell: (info) => formatInteger(info.getValue()),
    enableSorting: true,
  }),
  accessor("percentFilled", {
    id: "percentFilled",
    header: "% Filled",
    cell: (info) => info.getValue(),
  }),
];
